import { useRef, useState, useEffect, useCallback } from 'react';
import {
	Autocomplete,
	Box,
	Button,
	Chip,
	CircularProgress,
	IconButton,
	Menu,
	MenuItem,
	Checkbox,
	ListItemText,
	TextField,
} from '@mui/material';
import { EasTooltip } from 'ui/EasTooltip';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { nanoid } from '@reduxjs/toolkit';
import { useLocation, useNavigate } from 'react-router';
import queryString from 'query-string';
import { useSelector } from 'react-redux';
import { CountryWithFlag } from 'ui/CountryWithFlag';
import { UserAvatar } from 'ui/UserAvatar';
import { getSeparateFilterValue } from 'utils/FilterParams';
import { getUsersList } from 'redux/reducers/user/selectors';
import { getErrorTypes } from 'redux/reducers/errorCorrections/selectors';
import { getEmailsTypesData } from 'redux/reducers/emails/selectors';
import { getSmsTypesData } from 'redux/reducers/sms/selectors';
import { IFilterWithType } from 'redux/types';
import { ECountryType, ETableStructureType, TTableStructure } from 'ui/EasTable/types';
import { EEmEnums } from 'redux/reducers/emManagement/constants';
import { DateRangeModal } from 'ui/DateRangeModal';
import { DATES_ID } from 'services/constants/dates-constants';
import { YearMonthPiker } from 'ui/YearMonthPiker';
import ViewWeekIcon from '@mui/icons-material/ViewWeek';
import { CountrySelectMenu } from 'ui/CountrySelectMenu';
import { TUser } from 'redux/reducers/user/types';
import { CodeOff, FilterListOff } from '@mui/icons-material';
import { COLORS } from 'services/constants/colors';
import { Search } from './Search';
import { TEasTableFilterPanelProps } from './types';
import { EmFilter } from './EmFilter';
import { SeparateFilter } from './SeparateFilter';

export const EasTableFilterPanel = <T,>({
	tableStructure,
	children,
	childrenToEnd,
	loading = false,
	onTableStructureChange,
	emFilter = false,
	childrenToStart,
}: TEasTableFilterPanelProps<T>) => {
	const navigate = useNavigate();
	const location = useLocation();
	const { search } = useLocation();
	const navigateParams = queryString.parse(search);
	const usersList = useSelector(getUsersList);
	const typeOfErrors = useSelector(getErrorTypes);
	const emailTypes = useSelector(getEmailsTypesData);
	const smsTypes = useSelector(getSmsTypesData);

	const initialTableStructure = useRef(tableStructure);

	const getFilterOptions = (item: any): [string | null, string][] | undefined => {
		if (item?.filter && Array.isArray(item.filter)) {
			return item.filter;
		}

		if (item?.type === ETableStructureType.ARRAY_ID && item?.enumName === EEmEnums.EMAIL_TYPE) {
			return (emailTypes ?? []).map((emailType) => [emailType.handle, emailType.description]);
		}

		if (item?.type === ETableStructureType.ARRAY_ID && item?.enumName === EEmEnums.SMS_TYPE) {
			return (smsTypes ?? []).map((smsType) => [smsType.handle, smsType.description]);
		}

		return undefined;
	};

	const getFilterLabel = (fieldName: string, value: any): string | null => {
		const column = tableStructure?.find?.((i) => i.selector === fieldName);
		if (!column) return value;

		const options = getFilterOptions(column);
		const found = options?.find(
			(option: [string | null, string]) => String(option[0]) === String(value),
		);

		return found?.[1] ?? value;
	};

	const compareStructures = useCallback((a: unknown, b: unknown) => {
		return JSON.stringify(a) !== JSON.stringify(b);
	}, []);

	useEffect(() => {
		const savedStructure = localStorage.getItem(`tableStructure_${location.pathname}`);
		if (savedStructure && onTableStructureChange) {
			try {
				const parsedStructure = JSON.parse(savedStructure) as Array<{
					selector: string;
					show: boolean;
				}>;
				// Merge only visibility flags by selector to avoid injecting unserializable fields (icons/buttons/...)
				const mergedStructure = tableStructure.map((col) => {
					const saved = parsedStructure.find(
						(item) => item && item.selector === (col.selector as string),
					);
					return saved ? { ...col, show: !!saved.show } : col;
				});
				if (compareStructures(mergedStructure, tableStructure)) {
					onTableStructureChange(mergedStructure);
				}
			} catch {
				// ignore malformed saved structure
			}
		}
	}, [location.pathname, onTableStructureChange, tableStructure, compareStructures]);

	const isSorted = navigateParams?.sortIndex && navigateParams?.sortDirection;

	const filtersArray: IFilterWithType[] = tableStructure.reduce((acc, item) => {
		if (navigateParams[item.selector as string]) {
			const param = navigateParams[item.selector as string];
			acc.push({
				fieldName: item.selector as string,
				value: (item?.filter?.find((i) => i[0] === param)?.[0] ?? param) as
					| string
					// | number
					// | string[]
					// | number[]
					| null,
				type: item.type,
			});
		}
		return acc;
	}, [] as IFilterWithType[]);

	/** The param a cleared separate filter is left with: empty for a defaulted one, gone otherwise. */
	const getClearedSeparateFilterValue = (item: TTableStructure<T>): string | undefined =>
		item.defaultSeparateFilterValue !== undefined ? '' : undefined;

	const hasSeparateFilterValue = tableStructure.some(
		(item) => item.separateFilter && !!getSeparateFilterValue(item, navigateParams),
	);

	const [openDateRange, setOpenDateRange] = useState(false);
	const handleCloseDateRange = () => {
		setOpenDateRange(false);
	};

	const refAddFilter = useRef<HTMLButtonElement>(null);
	const refNewFilter = useRef<HTMLButtonElement>(null);
	const refFilterButtons = useRef<HTMLButtonElement>(null);

	const [newFilterIndex, setNewFilterIndex] = useState<number | null>(null);
	const [showTempFilterButton, setShowTempFilterButton] = useState<boolean>(false);
	const [open, setOpen] = useState(false);
	const [openFilterList, setOpenFilterList] = useState(false);
	const [amountInputValue, setAmountInputValue] = useState<string>('');
	const [stringInputValue, setStringInputValue] = useState<string>('');
	// When an already applied filter is edited, its chip becomes the anchor of the value menu.
	const [editFilterAnchorEl, setEditFilterAnchorEl] = useState<HTMLElement | null>(null);
	const handleClick = () => {
		setOpen(true);
	};
	const handleClose = () => {
		setOpen(false);
	};
	const handleCloseFilterList = () => {
		setOpenFilterList(false);
		setShowTempFilterButton(false);
		setEditFilterAnchorEl(null);
	};
	const [openYearMonthPiker, setOpenYearMonthPiker] = useState(false);
	const handleCloseYearMonthPiker = () => {
		setOpenYearMonthPiker(false);
		setShowTempFilterButton(false);
		setEditFilterAnchorEl(null);
	};

	/** A filter can be re-opened for editing only if there is a control to pick a new value with. */
	const isFilterEditable = (fieldName: string): boolean => {
		const column = tableStructure?.find?.((i) => i.selector === fieldName);
		if (!column) return false;
		if ((getFilterOptions(column)?.length ?? 0) > 0) return true;

		return (
			column.type === ETableStructureType.AMOUNT ||
			column.type === ETableStructureType.STRING ||
			column.type === ETableStructureType.DATE_MONTH ||
			column.type === ETableStructureType.USER ||
			column.type === ETableStructureType.COUNTRY_CODE ||
			column.type === ETableStructureType.COUNTRY_CODE_EU ||
			column.type === ETableStructureType.COUNTRY ||
			column.type === ETableStructureType.COUNTRY_EU
		);
	};

	const handleNewFilter = (index: number, anchorEl: HTMLElement | null = null) => {
		setNewFilterIndex(index);
		setEditFilterAnchorEl(anchorEl);
		// The dashed placeholder button is only needed while picking a value for a brand new filter.
		setShowTempFilterButton(!anchorEl);
		handleClose();
		if (tableStructure[index].type === ETableStructureType.DATE_MONTH) {
			setOpenYearMonthPiker(true);
			return;
		}

		if (tableStructure[index].type === ETableStructureType.AMOUNT) {
			const existing = filtersArray.find(
				(item) => item?.fieldName === (tableStructure[index].selector as string),
			);
			setAmountInputValue(existing?.value ? String(existing.value) : '');
		}

		if (tableStructure[index].type === ETableStructureType.STRING) {
			const existing = filtersArray.find(
				(item) => item?.fieldName === (tableStructure[index].selector as string),
			);
			setStringInputValue(existing?.value ? String(existing.value) : '');
		}

		setOpenFilterList(true);
	};

	/** Re-open the value picker of an already applied filter, anchored to its chip. */
	const handleEditFilter = (fieldName: string, anchorEl: HTMLElement | null) => {
		if (!isFilterEditable(fieldName)) return;
		const index = tableStructure.findIndex((i) => i.selector === fieldName);
		if (index < 0) return;
		handleNewFilter(index, anchorEl);
	};

	const filtersNavigate = (
		newFiltersArray: IFilterWithType[],
		/** Separate filter params to write instead of the ones currently in the url, keyed by param. */
		separateFilterValues?: Record<string, string>,
	) => {
		const searchTmp = newFiltersArray.reduce(
			(acc, item) => {
				if (item?.fieldName && item?.value === null) {
					acc[String(item.fieldName)] = 'null';
					return acc;
				}
				if (item?.fieldName && item?.value !== null) {
					acc[String(item.fieldName)] = item.value;
					if (
						(item.type === ETableStructureType.DATE ||
							item.type === ETableStructureType.DATE_RANGE) &&
						item.value === DATES_ID.CUSTOM_RANGE
					) {
						acc[`${item.fieldName}StartDate`] = navigateParams[
							`${item.fieldName}StartDate`
						] as string;
						acc[`${item.fieldName}EndDate`] = navigateParams[`${item.fieldName}EndDate`] as string;
					}
				}
				return acc;
			},
			{} as { [key: string]: string | number | string[] | number[] },
		);
		if (navigateParams?.sortIndex && navigateParams?.sortDirection) {
			searchTmp.sortIndex = navigateParams.sortIndex as string;
			searchTmp.sortDirection = navigateParams.sortDirection as string;
		}
		if (navigateParams?.search) {
			searchTmp.search = navigateParams.search as string;
		}
		if (navigateParams?.searchEmId) {
			searchTmp.searchEmId = navigateParams.searchEmId as string;
		}
		tableStructure
			.filter((item) => item.separateFilter)
			.forEach((item) => {
				const paramKey = `f-${String(item.selector)}`;
				const value = separateFilterValues?.[paramKey] ?? (navigateParams[paramKey] as string);
				if (value) {
					searchTmp[paramKey] = value;
					return;
				}
				// An empty param must survive, otherwise the column default would filter the table again.
				if (value === '' && item.defaultSeparateFilterValue !== undefined) {
					searchTmp[paramKey] = '';
				}
			});
		navigate({
			pathname: location.pathname,
			search: queryString.stringify(searchTmp),
		});
	};

	const handleApplyAmount = () => {
		if (newFilterIndex === null) return;
		const selector = tableStructure[newFilterIndex].selector as string;
		const valueRaw = amountInputValue?.trim();
		if (valueRaw === '') return;
		const valueNum = Number(valueRaw);
		const value: string | null = Number.isNaN(valueNum) ? valueRaw : String(valueNum);
		const newFiltersArray = [...filtersArray];
		const filterIndex = newFiltersArray.findIndex((item) => item?.fieldName === selector);
		if (filterIndex > -1 && newFiltersArray?.[filterIndex]) {
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore: ensure assignment for union type
			newFiltersArray[filterIndex].value = value;
		} else {
			newFiltersArray.push({
				fieldName: selector,
				value,
				type: tableStructure[newFilterIndex].type,
			});
		}
		setNewFilterIndex(null);
		handleCloseFilterList();
		filtersNavigate(newFiltersArray);
	};

	const handleApplyString = () => {
		if (newFilterIndex === null) return;
		const selector = tableStructure[newFilterIndex].selector as string;
		const value = stringInputValue?.trim();
		if (value === '') return;
		const newFiltersArray = [...filtersArray];
		const filterIndex = newFiltersArray.findIndex((item) => item?.fieldName === selector);
		if (filterIndex > -1 && newFiltersArray?.[filterIndex]) {
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore: ensure assignment for union type
			newFiltersArray[filterIndex].value = value;
		} else {
			newFiltersArray.push({
				fieldName: selector,
				value,
				type: tableStructure[newFilterIndex].type,
			});
		}
		setNewFilterIndex(null);
		handleCloseFilterList();
		filtersNavigate(newFiltersArray);
	};

	const handleSelectFilter = (index: number) => {
		if (newFilterIndex === null) return;
		const column = tableStructure[newFilterIndex];
		const options = getFilterOptions(column) ?? [];

		const newFiltersArray = [...filtersArray];
		handleCloseFilterList();

		if (
			(column.type === ETableStructureType.DATE ||
				column.type === ETableStructureType.DATE_RANGE) &&
			options[index] &&
			options[index][0] === DATES_ID.CUSTOM_RANGE
		) {
			setOpenDateRange(true);
			const existingIndex = newFiltersArray.findIndex(
				(item) => item?.fieldName === column.selector,
			);
			if (existingIndex > -1) {
				// eslint-disable-next-line @typescript-eslint/ban-ts-comment
				// @ts-ignore: ensure assignment for union type
				newFiltersArray[existingIndex].value = options[index]?.[0] ?? '';
			} else {
				newFiltersArray.push({
					fieldName: column.selector as string,
					value: options[index]?.[0] ?? '',
					type: column.type,
				});
			}
			filtersNavigate(newFiltersArray);

			return;
		}
		if (column.type === ETableStructureType.DATE_MONTH) {
			setOpenYearMonthPiker(true);
			return;
		}
		const filterIndex = newFiltersArray?.findIndex?.((item) => item?.fieldName === column.selector);
		if (filterIndex > -1 && !!newFiltersArray?.[filterIndex]) {
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore: Unreachable code error
			newFiltersArray[filterIndex].value = options[index]?.[0] ?? '';
		} else {
			newFiltersArray.push({
				fieldName: column.selector as string,
				value: options[index]?.[0] ?? '',
				type: column.type,
			});
		}
		// applied filter update
		if (newFiltersArray) {
			setNewFilterIndex(null);
			filtersNavigate(newFiltersArray);
		}
	};

	const handleSelectUser = (userId: string | number) => {
		if (newFilterIndex === null) return;
		handleCloseFilterList();
		const newFiltersArray = [...filtersArray];
		const filterIndex = newFiltersArray?.findIndex?.(
			(item) => item?.fieldName === tableStructure[newFilterIndex].selector,
		);
		if (filterIndex > -1 && !!newFiltersArray?.[filterIndex]) {
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore: Unreachable code error
			newFiltersArray[filterIndex].value = userId;
		} else {
			newFiltersArray.push({
				fieldName: tableStructure[newFilterIndex].selector as string,
				value: String(userId),
				type: tableStructure[newFilterIndex].type,
			});
		}
		if (newFiltersArray) {
			setNewFilterIndex(null);
			filtersNavigate(newFiltersArray);
		}
	};

	const handleSelectCountry = (country: string) => {
		if (newFilterIndex === null) return;
		handleCloseFilterList();
		const newFiltersArray = [...filtersArray];
		const filterIndex = newFiltersArray?.findIndex?.(
			(item) => item?.fieldName === tableStructure[newFilterIndex].selector,
		);
		if (filterIndex > -1 && !!newFiltersArray?.[filterIndex]) {
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore: Unreachable code error
			newFiltersArray[filterIndex].value = country;
		} else {
			newFiltersArray.push({
				fieldName: tableStructure[newFilterIndex].selector as string,
				value: country,
				type: tableStructure[newFilterIndex].type,
			});
		}
		if (newFiltersArray) {
			setNewFilterIndex(null);
			filtersNavigate(newFiltersArray);
		}
	};

	const handleRemoveFilter = (index: number) => {
		const newFiltersArray = [...filtersArray];
		newFiltersArray.splice(index, 1);
		filtersNavigate(newFiltersArray);
	};

	const handleClearAll = () => {
		// const newFiltersArray = [];
		// const externalOrderIdItem = filtersArray.find((item) => item.fieldName === 'id');
		// if (externalOrderIdItem) {
		// 	newFiltersArray.push(externalOrderIdItem);
		// }
		// filtersNavigate(newFiltersArray);
		const searchTmp: Record<string, string> = {};
		if (navigateParams?.sortIndex && navigateParams?.sortDirection) {
			searchTmp.sortIndex = navigateParams.sortIndex as string;
			searchTmp.sortDirection = navigateParams.sortDirection as string;
		}
		tableStructure
			.filter((item) => item.separateFilter)
			.forEach((item) => {
				const cleared = getClearedSeparateFilterValue(item);
				if (cleared !== undefined) {
					searchTmp[`f-${String(item.selector)}`] = cleared;
				}
			});

		navigate({
			pathname: location.pathname,
			search: queryString.stringify(searchTmp),
		});
	};

	const [openColumnsMenu, setOpenColumnsMenu] = useState(false);
	const refColumnsButton = useRef<HTMLButtonElement>(null);

	const handleToggleColumn = (index: number) => {
		const newTableStructure = [...tableStructure];
		newTableStructure[index] = {
			...newTableStructure[index],
			show: !newTableStructure[index].show,
		};

		const isDifferent = JSON.stringify(newTableStructure) !== JSON.stringify(tableStructure);
		if (isDifferent) {
			localStorage.setItem(
				`tableStructure_${location.pathname}`,
				JSON.stringify(newTableStructure),
			);
			onTableStructureChange?.(newTableStructure);
		}
	};

	const handleUncheckAll = () => {
		const uncheckAllStructure = tableStructure.map((column) => ({
			...column,
			show: false,
		}));

		const isDifferent = JSON.stringify(uncheckAllStructure) !== JSON.stringify(tableStructure);
		if (isDifferent) {
			localStorage.removeItem(`tableStructure_${location.pathname}`);
			onTableStructureChange?.(uncheckAllStructure);
		}
	};

	const handleClearSort = () => {
		navigate({
			pathname: location.pathname,
			search: queryString.stringify({
				...navigateParams,
				sortIndex: undefined,
				sortDirection: undefined,
			}),
		});
	};

	const handleCheckAll = () => {
		const checkAllStructure = tableStructure.map((column) => ({
			...column,
			show: true,
		}));

		const isDifferent = JSON.stringify(checkAllStructure) !== JSON.stringify(tableStructure);
		if (isDifferent) {
			localStorage.removeItem(`tableStructure_${location.pathname}`);
			onTableStructureChange?.(checkAllStructure);
		}
	};

	const handleSetDefault = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();

		const isDifferent =
			JSON.stringify(initialTableStructure.current) !== JSON.stringify(tableStructure);
		if (isDifferent) {
			localStorage.removeItem(`tableStructure_${location.pathname}`);
			onTableStructureChange?.(initialTableStructure.current);
		}
		setOpenColumnsMenu(false);
	};

	/** Separate filters carrying a default, as the `f-<selector>` params they are read from. */
	const defaultSeparateFilterParams = (): Record<string, string> =>
		tableStructure.reduce(
			(acc, item) => {
				if (item.separateFilter && item.defaultSeparateFilterValue) {
					acc[`f-${String(item.selector)}`] = item.defaultSeparateFilterValue;
				}
				return acc;
			},
			{} as Record<string, string>,
		);

	const handleDefaultFilter = () => {
		const defaultFilters = tableStructure.filter((item) => item.defaultFilterValue);
		const defaultSeparateFilters = defaultSeparateFilterParams();
		if (defaultFilters.length > 0 || Object.keys(defaultSeparateFilters).length > 0) {
			const newFiltersArray: IFilterWithType[] = [];
			const externalOrderIdItem = filtersArray.find((item) => item.fieldName === 'id');
			if (externalOrderIdItem) {
				newFiltersArray.push(externalOrderIdItem);
			}

			defaultFilters.forEach((defaultFilter) => {
				if (defaultFilter.defaultFilterValue) {
					newFiltersArray.push({
						fieldName: defaultFilter.selector as string,
						value: defaultFilter.defaultFilterValue,
						type: defaultFilter.type,
					});
				}
			});

			// Separate filters without a default keep the value they already have in the url.
			filtersNavigate(newFiltersArray, defaultSeparateFilters);
		}
	};

	useEffect(() => {
		if (navigateParams?.defaultFilter) {
			handleDefaultFilter();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [navigateParams]);

	return (
		<Box
			style={{
				padding: '8px',
				color: COLORS.PRIMARY,
				fontWeight: 'bold',
				display: 'flex',
				flexDirection: 'row',
				justifyContent: 'space-between',
				gap: 2,
			}}
		>
			{children && children}
			{tableStructure.length > 0 ? (
				<Box sx={{ display: 'flex', gap: 1, minHeight: '28px' }}>
					{isSorted && (
						<EasTooltip title="Clear sort">
							<IconButton onClick={handleClearSort} color="primary" sx={{ p: 0, m: 0 }}>
								<CodeOff
									sx={{ p: 0, m: 0, rotate: '90deg', transform: 'scaleX(-1)' }}
									color="primary"
									fontSize="small"
								/>
							</IconButton>
						</EasTooltip>
					)}

					{(filtersArray.some((item) => !!item) ||
						!!navigateParams?.searchEmId ||
						!!navigateParams?.search ||
						hasSeparateFilterValue) && (
						<EasTooltip title="Clear all filters">
							<IconButton onClick={handleClearAll} color="primary" sx={{ p: 0, m: 0 }}>
								<FilterListOff sx={{ p: 0, m: 0 }} color="primary" fontSize="small" />
							</IconButton>
						</EasTooltip>
					)}
					{tableStructure.find(
						(item) =>
							item.defaultFilterValue || (item.separateFilter && item.defaultSeparateFilterValue),
					) && (
						<Button
							color="primary"
							sx={{
								fontWeight: 'bold',
								textTransform: 'none',
								p: 0,
								m: 0,
								borderRadius: '12px',
								textDecoration: 'underline',
								textAlign: 'left',
								lineHeight: 1.1,
								verticalAlign: 'top',
								alignItems: 'center',
							}}
							onClick={() => handleDefaultFilter()}
						>
							Default Filters
						</Button>
					)}

					{filtersArray?.map?.((item, index) => {
						if (!item) return null;
						if (
							(item.fieldName === 'id' &&
								tableStructure.find((i) => i.selector === 'id')?.searchable === true) ||
							(item.fieldName === 'external_order_id' &&
								tableStructure.find((i) => i.selector === 'external_order_id')?.searchable ===
									true) ||
							tableStructure.find((i) => i.selector === item.fieldName)?.separateFilter === true
						)
							return null;
						const editable = isFilterEditable(item.fieldName);
						const chip = (
							<Box
								key={String(item.fieldName)}
								color="primary"
								onClick={
									editable
										? (e: React.MouseEvent<HTMLElement>) =>
												handleEditFilter(item.fieldName, e.currentTarget)
										: undefined
								}
								sx={{
									display: 'inline-flex',
									alignItems: 'center',
									fontSize: '0.9rem',
									fontWeight: 500,
									textTransform: 'none',
									borderRadius: '12px',
									padding: '0px 10px',
									gap: '3px',
									border: `1px solid ${COLORS.PRIMARY}`,
									lineHeight: 1.2,
									verticalAlign: 'top',
									textAlign: 'left',
									cursor: editable ? 'pointer' : 'default',
									'&:hover': editable ? { backgroundColor: COLORS.BACKGROUND } : undefined,
								}}
							>
								{tableStructure?.find?.((i) => i.selector === item.fieldName)?.name}:{' '}
								<span style={{ fontWeight: 'bolder', marginLeft: '5px' }}>
									{item.value === DATES_ID.CUSTOM_RANGE ? (
										navigateParams[`${item.fieldName}StartDate`] &&
										navigateParams[`${item.fieldName}EndDate`] && (
											<>
												{navigateParams[`${item.fieldName}StartDate`]} -{' '}
												{navigateParams[`${item.fieldName}EndDate`]}
											</>
										)
									) : item.type === ETableStructureType.COUNTRY ||
									  item.type === ETableStructureType.COUNTRY_CODE_ARRAY ||
									  item.type === ETableStructureType.COUNTRY_CODE ||
									  item.type === ETableStructureType.COUNTRY_CODE_EU ||
									  item.type === ETableStructureType.COUNTRY_EU ? (
										<CountryWithFlag
											countryCode={String(item.value)}
											size="s"
											sx={{ fontWeight: 'bolder' }}
										/>
									) : item.fieldName === 'typeOfError' ? (
										<Chip
											label={
												typeOfErrors?.find((i) => i.id === +(item?.value ?? 0))?.name ?? 'Unknown'
											}
											size="small"
											// variant="outlined"
											color="error"
											sx={{
												p: '0 5px',
												fontSize: '.8rem',
												height: '20px',
												bgcolor: 'error.light',
												// color: '#00000090',
												fontWeight: '550',
											}}
										/>
									) : item.type === ETableStructureType.USER ? (
										<UserAvatar
											user={usersList?.find((i) => i.id === item?.value) ?? null}
											size="xs"
											onClick={() => null}
										/>
									) : (
										(tableStructure
											?.find?.((i) => i.selector === item.fieldName)
											?.filter?.find?.(
												(i: [string | number | null, string]) => i[0] === item.value,
											)?.[1] ?? item.value)
									)}
								</span>
								<IconButton
									style={{ padding: 0 }}
									aria-label="Remove filter"
									onClick={(e) => {
										e.stopPropagation();
										handleRemoveFilter(index);
									}}
								>
									<CloseIcon sx={{ fontSize: '1rem', fontWeight: 'bold' }} color="primary" />
								</IconButton>
							</Box>
						);

						if (!editable) return chip;

						return (
							<EasTooltip key={String(item.fieldName)} title="Click to change the filter value">
								{chip}
							</EasTooltip>
						);
					})}
					<Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }} ref={refFilterButtons}>
						{newFilterIndex !== null && showTempFilterButton && (
							<Button
								ref={refNewFilter}
								color="primary"
								style={{
									fontSize: '0.9rem',
									fontWeight: 500,
									padding: '0px 10px',
									textTransform: 'none',
									borderRadius: '12px',
									borderWidth: '1px',
									borderStyle: 'dashed',
									borderColor: 'primary.main',
									display: 'flex',
									alignItems: 'center',
									gap: 1,
									lineHeight: 1.2,
									textAlign: 'left',
									height: '100%',
								}}
							>
								{tableStructure[newFilterIndex].name} <ExpandMoreIcon fontSize="small" />
							</Button>
						)}
						{(tableStructure?.filter((item) => item.filterable)?.length ?? 0) > 0 && (
							<Button
								ref={refAddFilter}
								color="primary"
								sx={{
									fontWeight: 550,
									fontSize: '0.9rem',
									padding: '0px 10px',
									textTransform: 'none',
									borderRadius: '12px',
									borderWidth: '1px',
									borderStyle: 'dashed',
									borderColor: 'primary.main',
									lineHeight: 1.2,
									alignItems: 'center',
									textAlign: 'left',
									height: '100%',
								}}
								onClick={handleClick}
							>
								Add filter +
							</Button>
						)}

						<Menu
							anchorEl={refAddFilter.current}
							open={open}
							onClose={handleClose}
							MenuListProps={{
								'aria-labelledby': 'basic-button',
							}}
							sx={{
								'& .MuiMenu-paper': { borderRadius: '12px' },
							}}
						>
							{tableStructure?.map?.((item, index) => {
								if (!item.filterable) return null;
								return (
									<MenuItem
										key={nanoid()}
										onClick={() => handleNewFilter(index)}
										color="primary"
										sx={{
											color: 'primary.main',
											fontWeight: 'bold',
										}}
									>
										{item.name}
									</MenuItem>
								);
							})}
						</Menu>
						<Menu
							anchorEl={editFilterAnchorEl ?? refFilterButtons.current}
							open={openFilterList}
							onClose={handleCloseFilterList}
							MenuListProps={{
								'aria-labelledby': 'basic-button',
							}}
							sx={{
								'& .MuiMenu-paper': { borderRadius: '12px', marginLeft: '0px' },
							}}
						>
							{(() => {
								const column = tableStructure?.[newFilterIndex ?? 0];
								if (!column) return null;
								// A currency is picked by typing instead, out of the block further down.
								if (column.type === ETableStructureType.CURRENCY) return null;

								const options = getFilterOptions(column) ?? [];

								return options.map(
									(optionItem: [string | number | null, string], index: number) => {
										if (!optionItem) return null;
										return (
											<MenuItem
												key={nanoid()}
												onClick={() => handleSelectFilter(index)}
												selected={
													String(navigateParams[column.selector as string] ?? '') ===
													String(optionItem[0])
												}
												color="primary"
												sx={{ color: 'primary.main', fontWeight: 'bold' }}
											>
												{column.type === ETableStructureType.USER ? (
													<UserAvatar
														user={usersList?.find((i) => i.id === optionItem[0]) ?? null}
														size="xs"
														onClick={() => null}
													/>
												) : (
													optionItem[1]
												)}
											</MenuItem>
										);
									},
								);
							})()}
							{tableStructure?.[newFilterIndex ?? 0]?.type === ETableStructureType.AMOUNT && (
								<MenuItem sx={{ width: '320px', p: '8px 12px', display: 'block' }}>
									<TextField
										fullWidth
										type="number"
										size="small"
										label={tableStructure?.[newFilterIndex ?? 0]?.name ?? 'Amount'}
										placeholder="Enter amount"
										value={amountInputValue}
										onChange={(e) => setAmountInputValue(e.target.value)}
										onKeyDown={(e) => {
											// Prevent MenuList type-ahead from stealing focus while typing
											e.stopPropagation();
											if (e.key === 'Enter') handleApplyAmount();
										}}
									/>
									<Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, gap: 1 }}>
										<Button variant="outlined" size="small" onClick={handleCloseFilterList}>
											Cancel
										</Button>
										<Button variant="contained" size="small" onClick={handleApplyAmount}>
											Apply
										</Button>
									</Box>
								</MenuItem>
							)}
							{(tableStructure?.[newFilterIndex ?? 0]?.type === ETableStructureType.COUNTRY_CODE ||
								tableStructure?.[newFilterIndex ?? 0]?.type === ETableStructureType.COUNTRY ||
								tableStructure?.[newFilterIndex ?? 0]?.type ===
									ETableStructureType.COUNTRY_CODE_EU ||
								tableStructure?.[newFilterIndex ?? 0]?.type === ETableStructureType.COUNTRY_EU) && (
								<MenuItem
									sx={{ width: '300px', p: '0 10px' }}
									// Otherwise the menu list type-ahead grabs the typed letter and moves focus
									// onto this item (its label starts with "C"), so countries like "Canada"
									// could never be typed. Only printable keys are swallowed, Escape/Tab still work.
									onKeyDown={(event) => {
										if (event.key.length === 1) event.stopPropagation();
									}}
								>
									<CountrySelectMenu
										onChange={(value) => handleSelectCountry(value)}
										countryCode={String(
											navigateParams[tableStructure?.[newFilterIndex ?? 0]?.selector as string] ??
												tableStructure?.[newFilterIndex ?? 0]?.filter?.[0]?.[0] ??
												'',
										)}
										label={tableStructure?.[newFilterIndex ?? 0]?.name ?? ''}
										typeOfCountry={
											tableStructure?.[newFilterIndex ?? 0]?.type ===
												ETableStructureType.COUNTRY_CODE_EU ||
											tableStructure?.[newFilterIndex ?? 0]?.type === ETableStructureType.COUNTRY_EU
												? ECountryType.EU
												: ECountryType.ALL
										}
									/>
								</MenuItem>
							)}
							{tableStructure?.[newFilterIndex ?? 0]?.type === ETableStructureType.USER &&
								usersList?.map((user: TUser) => (
									<MenuItem
										key={user.id}
										sx={{ width: '300px', p: '5px 10px' }}
										selected={
											String(
												navigateParams[tableStructure?.[newFilterIndex ?? 0]?.selector as string] ??
													'',
											) === String(user.id)
										}
										onClick={() => handleSelectUser(user.id)}
									>
										<UserAvatar user={user} size="xs" onClick={() => null} />
									</MenuItem>
								))}
							{tableStructure?.[newFilterIndex ?? 0]?.type === ETableStructureType.STRING && (
								<MenuItem sx={{ width: '320px', p: '8px 12px', display: 'block' }}>
									<TextField
										fullWidth
										type="text"
										size="small"
										label={tableStructure?.[newFilterIndex ?? 0]?.name ?? 'Value'}
										placeholder="Enter value"
										value={stringInputValue}
										onChange={(e) => setStringInputValue(e.target.value)}
										onKeyDown={(e) => {
											// Prevent MenuList type-ahead from stealing focus while typing
											e.stopPropagation();
											if (e.key === 'Enter') handleApplyString();
										}}
									/>
									<Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, gap: 1 }}>
										<Button variant="outlined" size="small" onClick={handleCloseFilterList}>
											Cancel
										</Button>
										<Button variant="contained" size="small" onClick={handleApplyString}>
											Apply
										</Button>
									</Box>
								</MenuItem>
							)}
							{(() => {
								const column = tableStructure?.[newFilterIndex ?? 0];
								if (column?.type !== ETableStructureType.CURRENCY) return null;

								const options = getFilterOptions(column) ?? [];
								const currentValue = String(navigateParams[column.selector as string] ?? '');
								const selected =
									options.find(
										(option: [string | number | null, string]) =>
											String(option[0]) === currentValue,
									) ?? null;

								return (
									<MenuItem
										sx={{ width: '260px', p: '8px 12px', display: 'block' }}
										// Otherwise the menu list swallows the typed keys for its own type-ahead.
										onKeyDown={(event) => event.stopPropagation()}
									>
										<Autocomplete<[string | number | null, string]>
											options={options}
											value={selected}
											onChange={(event, option) =>
												handleSelectFilter(options.findIndex((item) => item === option))
											}
											getOptionLabel={(option) => String(option[1])}
											isOptionEqualToValue={(option, value) =>
												String(option[0]) === String(value[0])
											}
											openOnFocus
											size="small"
											componentsProps={{ paper: { sx: { borderRadius: '12px' } } }}
											renderInput={(params) => (
												<TextField
													{...params}
													label={column.name ?? 'Currency'}
													placeholder={`Select ${column.name ?? 'currency'}`}
												/>
											)}
										/>
									</MenuItem>
								);
							})()}
						</Menu>
						<Menu
							anchorEl={editFilterAnchorEl ?? refAddFilter.current}
							open={openYearMonthPiker}
							onClose={handleCloseYearMonthPiker}
							MenuListProps={{
								'aria-labelledby': 'basic-button',
							}}
							sx={{
								'& .MuiMenu-paper': {
									borderRadius: '12px',
									marginLeft: editFilterAnchorEl ? '0px' : '-124px',
								},
							}}
						>
							<YearMonthPiker
								onClose={handleCloseYearMonthPiker}
								filterSelector={tableStructure?.[newFilterIndex ?? 0]?.selector as string}
							/>
						</Menu>
					</Box>
				</Box>
			) : (
				<Box />
			)}
			<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
				{loading && (
					<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
						<CircularProgress size={24} />
					</Box>
				)}
				{!!childrenToStart && childrenToStart}
				{tableStructure?.filter((item) => item.separateFilter) && (
					<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
						{tableStructure
							?.filter((item) => item.separateFilter)
							.map((item) => (
								<SeparateFilter
									key={String(item.selector)}
									name={item.name ?? 'Value'}
									selector={String(item.selector)}
									type={item.type}
									filter={item.filter}
									defaultValue={item.defaultSeparateFilterValue}
									autocomplete={item.separateFilterAutocomplete}
								/>
							))}
					</Box>
				)}
				{emFilter && <EmFilter />}
				{tableStructure?.find((item) => item.searchable) && (
					<Search
						searchParams={tableStructure
							?.filter((item) => item.searchable)
							.map((i) => i.name)
							?.join(', ')}
					/>
				)}
				{onTableStructureChange && (
					<>
						{/* <Button
								ref={refColumnsButton}
								color="primary"
								sx={{
									fontWeight: 550,
									fontSize: '0.9rem',
									padding: '2px 10px',
									textTransform: 'none',
									borderRadius: '12px',
									borderWidth: '1px',
									borderStyle: 'dashed',
									borderColor: 'primary.main',
									display: 'flex',
									// alignItems: 'start',
									gap: 1,
									textAlign: 'left',
									lineHeight: 1.2,
								}}
								onClick={() => setOpenColumnsMenu(true)}
							>
								<ViewWeekIcon fontSize="small" />
								Edit columns
							</Button> */}
						{!!childrenToEnd && childrenToEnd}
						<EasTooltip title="Edit columns">
							<IconButton
								onClick={() => setOpenColumnsMenu(true)}
								color="primary"
								ref={refColumnsButton}
								sx={{
									p: 0,
									m: 0,
									verticalAlign: 'middle',
								}}
							>
								<ViewWeekIcon fontSize="small" />
							</IconButton>
						</EasTooltip>
						<Menu
							anchorEl={refColumnsButton.current}
							open={openColumnsMenu}
							onClose={() => setOpenColumnsMenu(false)}
							MenuListProps={{
								'aria-labelledby': 'columns-button',
							}}
							sx={{
								'& .MuiMenu-paper': { borderRadius: '12px' },
							}}
						>
							<MenuItem
								onClick={handleCheckAll}
								sx={{
									color: 'primary.main',
									fontSize: '0.8rem',
									m: 0,
									p: '0 0 5px 10px',
									fontWeight: 550,
									maxHeight: '25px',
								}}
							>
								Check all
							</MenuItem>
							<MenuItem
								onClick={handleUncheckAll}
								sx={{
									color: 'primary.main',
									fontSize: '0.8rem',
									m: 0,
									p: '0 0 5px 10px',
									fontWeight: 550,
									maxHeight: '25px',
								}}
							>
								Uncheck all
							</MenuItem>
							<MenuItem
								onClick={handleSetDefault}
								sx={{
									color: 'primary.main',
									fontSize: '0.8rem',
									m: 0,
									p: '5px 0 10px 10px',
									fontWeight: 550,
									maxHeight: '25px',
								}}
							>
								Set default
							</MenuItem>
							{tableStructure.map((column, index) => (
								<MenuItem
									key={nanoid()}
									onClick={(e) => {
										e.preventDefault();
										e.stopPropagation();
										handleToggleColumn(index);
									}}
									sx={{ m: 0, p: 0, fontSize: '0.8rem', maxHeight: '25px' }}
								>
									<Checkbox
										checked={column.show}
										onClick={(e) => {
											e.preventDefault();
											e.stopPropagation();
											handleToggleColumn(index);
										}}
										size="small"
									/>
									<ListItemText
										primary={column.name}
										sx={{
											fontSize: '0.8rem',
											ml: 1,
											minHeight: '20px',
											'& .MuiListItemText-primary': { fontSize: '0.8rem' },
										}}
									/>
								</MenuItem>
							))}
						</Menu>
					</>
				)}
			</Box>
			<DateRangeModal
				open={openDateRange}
				setClose={handleCloseDateRange}
				selector={tableStructure?.[newFilterIndex ?? 0]?.selector as string}
			/>
		</Box>
	);
};
